import { Button } from "@dropbox/dig-components/buttons";
import { Menu } from "@dropbox/dig-components/menu";
import { UIIcon } from "@dropbox/dig-icons";
import { AddLine, EditLine, SliderLine } from "@dropbox/dig-icons/assets";
import { PAP_Click_VoiceMenuDropdown } from "@mirage/analytics/events/types/click_voice_menu_dropdown";
import { PAP_Click_VoiceMenuOption } from "@mirage/analytics/events/types/click_voice_menu_option";
import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import { AccessType } from "@mirage/shared/compose/assist-api";
import {
  DEFAULT_PRECONFIGURED_VOICE_ID,
  isPreConfiguredVoiceID,
  PRE_CONFIGURED_VOICE_IDS,
} from "@mirage/shared/compose/compose-session";
import { IconButtonWithTooltip } from "@mirage/shared/icons/IconButtonWithTooltip";
import i18n from "@mirage/translations";
import { memo, useMemo } from "react";
import styles from "./VoiceSelector.module.css";

import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import type { ComposeVoice } from "@mirage/shared/compose/compose-voice";

interface VoiceSelectorProps {
  customVoices: ComposeVoice[] | undefined;
  currentVoiceID: string | undefined;
  onChangeVoiceID: (voiceID: string) => void;
  onOpenVoiceSettings: (voiceID: string | undefined) => void;
  onCreateNewVoice: () => void;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: { actionSurfaceComponent?: ActionSurfaceComponent },
  ) => void;
}
export const VoiceSelector = memo(
  ({
    customVoices,
    currentVoiceID,
    onChangeVoiceID,
    onCreateNewVoice,
    onOpenVoiceSettings,
    logComposeEvent,
  }: VoiceSelectorProps) => {
    const tonesSharingEnabled =
      useFeatureFlagValue("dash_2024_12_17_assist_tones_sharing") === "ON";
    const { teamVoices, individualVoices } = useMemo(() => {
      if (!customVoices) {
        return { teamVoices: [], individualVoices: [] };
      }
      return {
        teamVoices: customVoices.filter(
          (voice) => voice.accessType === AccessType.TEAM,
        ),
        individualVoices: customVoices.filter(
          (voice) => voice.accessType === AccessType.INDIVIDUAL,
        ),
      };
    }, [customVoices]);
    return (
      <Menu.Wrapper
        onSelection={(value) => {
          if (value === NEW_STYLE_ID) {
            onCreateNewVoice();
          } else {
            onChangeVoiceID(value);
          }
          logComposeEvent(
            PAP_Click_VoiceMenuOption({
              actionType: value,
            }),
          );
        }}
      >
        {({ getContentProps, getTriggerProps }) => (
          <>
            <Button
              {...getTriggerProps()}
              variant="borderless"
              withDropdownIcon
              hasNoUnderline
              onClickCapture={() => {
                logComposeEvent(PAP_Click_VoiceMenuDropdown());
              }}
            >
              <UIIcon src={SliderLine} />
              {i18n.t("compose_tones_menu_title")}
            </Button>
            <Menu.Content {...getContentProps()}>
              <Menu.Segment>
                {PRE_CONFIGURED_VOICE_IDS.map((voiceID) => {
                  const labels = getVoiceLabels(voiceID, customVoices || []);
                  return (
                    <Menu.SelectItem
                      className={styles.voiceRow}
                      key={voiceID}
                      value={voiceID}
                      selected={voiceID === currentVoiceID}
                      withTitle={labels.title}
                      withSubtitle={labels.description}
                    />
                  );
                })}
              </Menu.Segment>
              {individualVoices.length > 0 && (
                <Menu.Segment
                  withLabel={i18n.t("compose_personal_tones_section")}
                >
                  {individualVoices.map((voice) => (
                    <Menu.SelectItem
                      className={styles.voiceRow}
                      key={voice.id}
                      value={voice.id}
                      selected={voice.id === currentVoiceID}
                      withRightAccessory={
                        <IconButtonWithTooltip
                          variant="borderless"
                          tooltipProps={{
                            title: i18n.t("edit"),
                          }}
                          onClick={(e: React.MouseEvent) => {
                            onOpenVoiceSettings(voice.id);
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          className={styles.editVoiceButton}
                        >
                          <UIIcon src={EditLine} />
                        </IconButtonWithTooltip>
                      }
                      withTitle={voice.name}
                      withSubtitle={voice.description}
                    />
                  ))}
                </Menu.Segment>
              )}
              {tonesSharingEnabled && teamVoices.length > 0 && (
                <Menu.Segment
                  className={styles.teamMenu}
                  withLabel={i18n.t("compose_team_tones_section")}
                >
                  <Menu.Submenu
                    withTriggerContent={i18n.t("compose_view_team_tones", {
                      count: teamVoices.length,
                      plural: teamVoices.length > 1 ? "s" : "",
                    })}
                  >
                    <Menu.Segment>
                      {teamVoices.map((voice) => (
                        <Menu.SelectItem
                          className={styles.teamMenuRow}
                          key={voice.id}
                          value={voice.id}
                          selected={voice.id === currentVoiceID}
                          withRightAccessory={
                            voice.is_owned && (
                              <IconButtonWithTooltip
                                variant="borderless"
                                tooltipProps={{
                                  title: i18n.t("edit"),
                                }}
                                onClick={(e: React.MouseEvent) => {
                                  onOpenVoiceSettings(voice.id);
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                className={styles.editVoiceButton}
                              >
                                <UIIcon src={EditLine} />
                              </IconButtonWithTooltip>
                            )
                          }
                          withTitle={voice.name}
                          withSubtitle={voice.description}
                        />
                      ))}
                    </Menu.Segment>
                  </Menu.Submenu>
                </Menu.Segment>
              )}

              <Menu.Segment>
                <Menu.SelectItem
                  className={styles.voiceRow}
                  value={NEW_STYLE_ID}
                  withLeftAccessory={<UIIcon src={AddLine} />}
                  withTitle={i18n.t("compose_context_form_create_tone")}
                />
              </Menu.Segment>
            </Menu.Content>
          </>
        )}
      </Menu.Wrapper>
    );
  },
);
VoiceSelector.displayName = "VoiceSelector";

const NEW_STYLE_ID = "NEW_STYLE";

interface VoiceLabels {
  title: string;
  description: string | undefined;
}
export function getVoiceLabels(
  voiceID: string | undefined,
  customVoices: ComposeVoice[],
): VoiceLabels {
  const v = voiceID || DEFAULT_PRECONFIGURED_VOICE_ID;
  if (isPreConfiguredVoiceID(v)) {
    switch (v) {
      case "preset_neutral":
        return {
          title: i18n.t("compose_preset_neutral_tones_label_title"),
          description: i18n.t("compose_preset_neutral_tones_label_description"),
        };
      case "preset_persuasive":
        return {
          title: i18n.t("compose_preset_persuasive_tones_label_title"),
          description: i18n.t(
            "compose_preset_persuasive_tones_label_description",
          ),
        };
      case "preset_instructional":
        return {
          title: i18n.t("compose_preset_instructional_tones_label_title"),
          description: i18n.t(
            "compose_preset_instructional_tones_label_description",
          ),
        };
      case "preset_narrative":
        return {
          title: i18n.t("compose_preset_narrative_tones_label_title"),
          description: i18n.t(
            "compose_preset_narrative_tones_label_description",
          ),
        };
      case "preset_informal":
        return {
          title: i18n.t("compose_preset_informal_tones_label_title"),
          description: i18n.t(
            "compose_preset_informal_tones_label_description",
          ),
        };
      case "preset_analytical":
        return {
          title: i18n.t("compose_preset_analytical_tones_label_title"),
          description: i18n.t(
            "compose_preset_analytical_tones_label_description",
          ),
        };
      default:
        v satisfies never;
        throw new Error(`Unsupported voice type: ${v}`);
    }
  }
  const customVoice = customVoices.find((voice) => voice.id === v);
  return {
    title: customVoice?.name || "",
    description: undefined,
  };
}
